/**
 * ContratoDeFabricacao — seção do Gate B com o contrato montado antes do codegen
 * (plano Contract-Driven, Onda 2 — SPEC-ONDA-2 FR-15/FR-17).
 *
 * Mostra as contagens, a rastreabilidade requisito → funcionalidade → tela → endpoint →
 * entidade e as lacunas do gate pré-geração. No modo estrito, cada bloqueio aberto ganha
 * uma caixa de justificativa: aprovar exige aceitar cada um (a API recusa com 409).
 *
 * Paleta C, estilos inline, pt-BR.
 */
import { useState } from 'react';
import { ChevronDown, ChevronRight, FileCheck } from 'lucide-react';
import { C } from '@/theme/palette';
import { Badge } from '@/components/ui';
import { RoteiroDeValidacao } from './RoteiroDeValidacao';

export const JUSTIFICATIVA_MINIMA = 10;

const ROTULOS = [
  ['requirements', 'Requisitos'], ['screens', 'Telas'], ['features', 'Funcionalidades'],
  ['endpoints', 'Endpoints'], ['entities', 'Entidades'], ['nfrs', 'NFRs'],
  ['sequences', 'Sequências'],
];

/** Bloqueios ainda sem aceite registrado no contrato. */
export function bloqueiosAbertos(contract) {
  return (contract?.gaps ?? []).filter((g) => g.severity === 'bloqueio' && !g.accepted);
}

function Titulo({ children }) {
  return (
    <h2 style={{
      fontSize: 12, fontWeight: 700, color: C.neutralMuted, letterSpacing: '0.08em',
      textTransform: 'uppercase', margin: 0,
    }}>
      {children}
    </h2>
  );
}

function Lista({ itens }) {
  if (!itens?.length) return <span style={{ color: C.neutralMuted }}>—</span>;
  return <span style={{ fontFamily: 'monospace', fontSize: 11.5 }}>{itens.join(', ')}</span>;
}

export function ContratoDeFabricacao({
  contract, contractMode, aceites, onAceite, editavel,
  onSalvarRoteiro, salvandoRoteiro, errosDoRoteiro,
}) {
  const [aberto, setAberto] = useState(true);
  const estrito = contractMode === 'strict';

  const cabecalho = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
      <FileCheck size={14} aria-hidden="true" style={{ color: C.cyber }} />
      <Titulo>Contrato de fabricação</Titulo>
      {contract && <Badge color={C.cyber} light>v{contract.version}</Badge>}
      <Badge color={estrito ? C.hot : C.neutralDim} light>
        {estrito ? 'Modo estrito' : 'Modo híbrido'}
      </Badge>
    </div>
  );

  if (!contract) {
    return (
      <section aria-label="Contrato de fabricação" style={{ marginBottom: 28 }}>
        {cabecalho}
        <div role="note" style={{
          padding: '14px 18px', fontSize: 13, color: C.neutralDim,
          border: `1px dashed ${estrito ? C.hot : C.blackBorder}`, borderRadius: 8,
        }}>
          O contrato de fabricação não foi montado para esta execução.
          {estrito
            ? ' No modo estrito, o codegen só começa com contrato: peça esclarecimento para remontá-lo.'
            : ' A execução segue, sem a conferência de especificação antes do código.'}
        </div>
      </section>
    );
  }

  const gaps = contract.gaps ?? [];
  const nBloqueios = gaps.filter((g) => g.severity === 'bloqueio').length;
  const nAvisos = gaps.filter((g) => g.severity === 'aviso').length;

  return (
    <section aria-label="Contrato de fabricação" style={{ marginBottom: 28 }}>
      {cabecalho}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
        {ROTULOS.map(([chave, rotulo]) => (
          <span key={chave} style={{
            fontSize: 11.5, padding: '4px 10px', borderRadius: 6,
            backgroundColor: C.blackElev, border: `1px solid ${C.blackBorder}`, color: C.neutralDim,
          }}>
            {rotulo}: <strong style={{ color: C.neutral }}>
              {(contract.counts?.[chave] ?? 0).toLocaleString('pt-BR')}
            </strong>
          </span>
        ))}
      </div>

      <div style={{ fontSize: 13, color: nBloqueios ? C.hot : C.neutralDim, marginBottom: 12 }}>
        {nBloqueios} bloqueio(s) · {nAvisos} aviso(s)
      </div>

      {/* Rastreabilidade */}
      <button
        type="button"
        onClick={() => setAberto((a) => !a)}
        aria-expanded={aberto}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none',
          color: C.neutralDim, cursor: 'pointer', fontSize: 12, padding: 0, marginBottom: 8,
        }}
      >
        {aberto ? <ChevronDown size={14} aria-hidden="true" /> : <ChevronRight size={14} aria-hidden="true" />}
        Rastreabilidade (requisito → funcionalidade → tela → endpoint → entidade)
      </button>
      {aberto && (
        <div style={{ overflowX: 'auto', marginBottom: 16 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ color: C.neutralMuted, textAlign: 'left' }}>
                {['Requisito', 'Funcionalidades', 'Telas', 'Endpoints', 'Entidades'].map((h) => (
                  <th key={h} style={{ padding: '6px 8px', borderBottom: `1px solid ${C.blackBorder}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(contract.traceability ?? []).map((l) => (
                <tr key={l.requirement} style={{ color: C.neutralDim }}>
                  <td style={{ padding: '6px 8px', borderBottom: `1px solid ${C.blackBorder}` }}>
                    <span style={{ fontFamily: 'monospace', color: C.cyber }}>{l.requirement}</span>
                    <div style={{ fontSize: 11, color: C.neutralMuted }}>{l.title}</div>
                  </td>
                  {[l.features, l.screens, l.endpoints, l.entities].map((itens, i) => (
                    <td key={i} style={{ padding: '6px 8px', borderBottom: `1px solid ${C.blackBorder}` }}>
                      <Lista itens={itens} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Lacunas do gate pré-geração */}
      {gaps.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 8 }}>
          {gaps.map((g) => {
            const bloqueio = g.severity === 'bloqueio';
            const pedeAceite = estrito && bloqueio && !g.accepted && editavel;
            return (
              <li key={g.id} style={{
                padding: '10px 12px', borderRadius: 8, backgroundColor: C.blackElev,
                border: `1px solid ${bloqueio && !g.accepted ? `${C.hot}66` : C.blackBorder}`,
              }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Badge color={bloqueio ? C.hot : C.warn} light>
                    {bloqueio ? 'BLOQUEIO' : 'AVISO'}
                  </Badge>
                  {g.accepted && <Badge color={C.cyber} light>ACEITO</Badge>}
                  <span style={{ fontSize: 12.5, color: C.neutral }}>{g.message}</span>
                </div>
                {pedeAceite && (
                  <textarea
                    aria-label={`Justificativa para aceitar ${g.target}`}
                    value={aceites?.[g.id] ?? ''}
                    onChange={(e) => onAceite?.(g.id, e.target.value)}
                    rows={2}
                    placeholder={`Aceitar e seguir: justifique (mínimo ${JUSTIFICATIVA_MINIMA} caracteres)`}
                    style={{
                      width: '100%', marginTop: 8, backgroundColor: C.blackElev2,
                      border: `1px solid ${C.blackBorder}`, borderRadius: 6, color: C.neutral,
                      fontSize: 12.5, padding: '8px 10px', fontFamily: 'inherit', boxSizing: 'border-box',
                    }}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Projeto de agente: ferramentas, limite de passos e roteiro de validação.
          `key` na versão: contrato novo (clarify) recomeça o formulário. */}
      <RoteiroDeValidacao
        key={contract.version}
        agent={contract.agent ?? null}
        requisitos={contract.traceability ?? []}
        editavel={editavel && contract.status === 'draft' && !!onSalvarRoteiro}
        onSalvar={onSalvarRoteiro}
        salvando={salvandoRoteiro}
        erros={errosDoRoteiro}
      />
    </section>
  );
}
